import type { ThreadEventPreview } from '../types'
import {
  type CallStartedEventData,
  type CallEndedEventData,
  type SmsMessageReceivedEventData,
  type SmsMessageSentEventData,
  type EmailMessageReceivedEventData,
  type EmailMessageSentEventData,
  type HandoffInitiatedEventData,
  type HandoffCompletedEventData,
  type PropertyDetectedEventData,
  type ContactGroupDetectedEventData,
  type SystemNotificationEventData,
  type PushIntegrationInitiatedEventData,
  type PushIntegrationCompletedEventData,
  type PushIntegrationFailedEventData,
  formatUSPhoneForDisplay,
  formatGroupTypeName,
  getActionSubtypeLabel,
  isSuppressionSystemNotification,
} from '@super/shared-types'

/**
 * Transform a thread event into a short preview string for the thread list
 * These are concise versions meant to show in the thread preview
 */
export function getThreadEventPreview(event: ThreadEventPreview): string {
  switch (event.eventType) {
    case 'CALL_STARTED': {
      const data = event.eventData as CallStartedEventData
      return `Call started from ${formatUSPhoneForDisplay(data.from)}`
    }

    case 'CALL_ENDED': {
      const data = event.eventData as CallEndedEventData
      // From the conference's own lifecycle stamps, not a stored duration —
      // omitted entirely when unknown rather than shown as "0 min" (SUP-2939).
      const seconds = data.duration
      if (!seconds || seconds <= 0) return 'Call ended'
      const minutes = Math.max(1, Math.round(seconds / 60))
      return `Call ended (${minutes} min)`
    }

    case 'CALL_RECORDING':
      return 'Recording available'

    case 'MESSAGE_RECEIVED': {
      const data = event.eventData as SmsMessageReceivedEventData
      return data.messageText
    }

    case 'EMAIL_MESSAGE_RECEIVED': {
      const data = event.eventData as EmailMessageReceivedEventData
      return data.messageText
    }

    case 'MESSAGE_SENT': {
      const data = event.eventData as SmsMessageSentEventData
      return data.messageText
    }

    case 'EMAIL_MESSAGE_SENT': {
      const data = event.eventData as EmailMessageSentEventData
      return data.messageText
    }

    case 'HANDOFF_INITIATED': {
      const data = event.eventData as HandoffInitiatedEventData
      return `Handoff: ${data.workflowName}`
    }

    case 'HANDOFF_COMPLETED': {
      const data = event.eventData as HandoffCompletedEventData
      return data.success
        ? `Handoff completed: ${data.workflowName}`
        : `Handoff failed: ${data.workflowName}`
    }

    case 'CALL_PROCESSING_RESULT':
      return 'Call summary available'

    case 'SMS_PROCESSING_RESULT':
      return 'SMS summary available'

    case 'EMAIL_PROCESSING_RESULT':
      return 'Email summary available'

    case 'HANDOFF_ACTION_EXECUTION':
      return 'Actions executed'

    case 'SYSTEM_NOTIFICATION': {
      const data = event.eventData as SystemNotificationEventData
      if (isSuppressionSystemNotification(data)) {
        return 'AI response suppressed'
      }
      return data.message
    }

    case 'PROPERTY_DETECTED': {
      const data = event.eventData as PropertyDetectedEventData
      const firstProperty = data.properties[0]
      if (!firstProperty) return 'Property detected'

      const name =
        firstProperty.propertyName ||
        firstProperty.propertyAddress ||
        firstProperty.propertyId ||
        'Unknown'
      const unit = firstProperty.unit ? `, Unit ${firstProperty.unit}` : ''

      if (data.properties.length > 1) {
        return `Property: ${name}${unit} +${data.properties.length - 1} more`
      }
      return `Property: ${name}${unit}`
    }

    case 'CONTACT_GROUP_DETECTED': {
      const data = event.eventData as ContactGroupDetectedEventData
      const firstGroup = data.contactGroups[0]
      if (!firstGroup) return 'Contact group detected'

      const name = firstGroup.groupType
        ? formatGroupTypeName(firstGroup.groupType)
        : firstGroup.customGroup || 'Unknown'

      if (data.contactGroups.length > 1) {
        return `Group: ${name} +${data.contactGroups.length - 1} more`
      }
      return `Group: ${name}`
    }

    case 'PUSH_INTEGRATION_INITIATED': {
      const data = event.eventData as PushIntegrationInitiatedEventData
      // Legacy events without actionSubtype will show generic message
      const label = data.actionSubtype
        ? getActionSubtypeLabel(data.actionSubtype)
        : null
      return label ? `Creating ${label}...` : 'Processing...'
    }

    case 'PUSH_INTEGRATION_COMPLETED': {
      const data = event.eventData as PushIntegrationCompletedEventData
      // Legacy events without actionSubtype will show generic message
      const label = data.actionSubtype
        ? getActionSubtypeLabel(data.actionSubtype)
        : null
      return label ? `${label} created` : 'Completed'
    }

    case 'PUSH_INTEGRATION_FAILED': {
      const data = event.eventData as PushIntegrationFailedEventData
      // Legacy events without actionSubtype will show generic message
      const label = data.actionSubtype
        ? getActionSubtypeLabel(data.actionSubtype)
        : null
      return label ? `${label} creation failed` : 'Failed'
    }

    case 'CONFERENCE_TRANSCRIPT':
      return 'Call transcript available'

    default:
      return 'Activity update'
  }
}
